import type { GearRequest } from '../backend/localBackend.ts';
import { GEAR_SLOTS, RARITIES, type GearSlot, type Item, type Rarity, type SaveData } from '../model/types.ts';
import {
  addToBag,
  canRecycle,
  enhanceItem,
  equipItem,
  previewRecycle,
  rarityRank,
  recycleReward,
  unequipItem,
} from '../sim/gear.ts';

function slotOf(value: unknown): GearSlot | null {
  if (typeof value !== 'string') return null;
  return GEAR_SLOTS.includes(value as GearSlot) ? (value as GearSlot) : null;
}

function rarityOf(value: unknown): Rarity | null {
  if (typeof value !== 'string') return null;
  return RARITIES.includes(value as Rarity) ? (value as Rarity) : null;
}

const ACTIONS = ['equip', 'unequip', 'salvage', 'enhance', 'lock', 'unlock', 'recycle_bulk', 'set_auto_recycle'] as const;

/** Equip, salvage, lock, and recycle against the save the server already stores. */
export function applyGearAction(data: SaveData, body: GearRequest | Record<string, unknown>): SaveData {
  const request = gearRequestFrom({ ...body });
  if (!request) throw new Error('save_invalid');
  return applyGearRequest(data, request);
}

export function gearRequestStillValid(data: SaveData, request: GearRequest): boolean {
  try {
    applyGearRequest(data, request);
    return true;
  } catch {
    return false;
  }
}

export function gearRequestFrom(body: Record<string, unknown>): GearRequest | null {
  const action = body.action;
  if (typeof action !== 'string' || !ACTIONS.includes(action as (typeof ACTIONS)[number])) return null;
  const itemId = typeof body.itemId === 'string' && body.itemId.length > 0 ? body.itemId : undefined;
  const slot = slotOf(body.slot) ?? undefined;
  if (action === 'set_auto_recycle') {
    if (body.rarity === null || body.rarity === undefined || body.rarity === '') return { action, rarity: null };
    const rarity = rarityOf(body.rarity);
    if (!rarity) return null;
    return { action, rarity };
  }
  if (action === 'recycle_bulk') {
    const rarity = rarityOf(body.rarity);
    if (!rarity) return null;
    return { action, rarity };
  }
  return { action: action as GearRequest['action'], itemId, slot };
}

function locate(data: SaveData, itemId: string): { item: Item; place: 'bag' | GearSlot } | null {
  const bagged = data.gear.bag.find((item) => item.id === itemId);
  if (bagged) return { item: bagged, place: 'bag' };
  for (const slot of GEAR_SLOTS) {
    const worn = data.gear.equipped[slot];
    if (worn?.id === itemId) return { item: worn, place: slot };
  }
  return null;
}

function applyGearRequest(data: SaveData, request: GearRequest): SaveData {
  const next = structuredClone(data);
  if (request.action === 'equip') {
    if (!request.itemId) throw new Error('save_invalid');
    const moved = equipItem(next.gear.equipped, next.gear.bag, request.itemId);
    if (!moved) throw new Error('save_invalid');
    next.gear.equipped = moved.equipped;
    next.gear.bag = moved.bag;
    return next;
  }
  if (request.action === 'unequip') {
    if (!request.slot) throw new Error('save_invalid');
    const moved = unequipItem(next.gear.equipped, next.gear.bag, request.slot);
    if (!moved) throw new Error('save_invalid');
    const bag = addToBag(moved.bag, []);
    next.gear.equipped = moved.equipped;
    next.gear.bag = bag.bag;
    next.currencies.gold += bag.gold;
    next.currencies.forgeDust += bag.dust;
    return next;
  }
  if (request.action === 'salvage') {
    if (!request.itemId) throw new Error('save_invalid');
    const item = next.gear.bag.find((candidate) => candidate.id === request.itemId);
    if (!item || !canRecycle(item, next.gear.equipped)) throw new Error('save_invalid');
    const reward = recycleReward(item);
    next.gear.bag = next.gear.bag.filter((candidate) => candidate.id !== request.itemId);
    next.currencies.gold += reward.gold;
    next.currencies.forgeDust += reward.forgeDust;
    return next;
  }
  if (request.action === 'lock' || request.action === 'unlock') {
    if (!request.itemId) throw new Error('save_invalid');
    const found = locate(next, request.itemId);
    if (!found) throw new Error('save_invalid');
    found.item.locked = request.action === 'lock';
    return next;
  }
  if (request.action === 'recycle_bulk') {
    if (!request.rarity) throw new Error('save_invalid');
    const preview = previewRecycle(next, request.rarity);
    if (preview.count < 1) throw new Error('save_invalid');
    const line = rarityRank(request.rarity);
    next.gear.bag = next.gear.bag.filter((item) => rarityRank(item.rarity) >= line || !canRecycle(item, next.gear.equipped));
    next.currencies.gold += preview.gold;
    next.currencies.forgeDust += preview.forgeDust;
    return next;
  }
  if (request.action === 'set_auto_recycle') {
    next.gear.autoRecycleBelow = request.rarity ?? null;
    return next;
  }
  if (!request.slot) throw new Error('save_invalid');
  const item = next.gear.equipped[request.slot];
  if (!item) throw new Error('save_invalid');
  const enhanced = enhanceItem(item, next.currencies);
  if (!enhanced.ok) throw new Error('save_invalid');
  next.gear.equipped = { ...next.gear.equipped, [request.slot]: enhanced.item };
  next.currencies = enhanced.wallet;
  return next;
}
